package org.solyton.solawi.bid.module.banking.data

import org.solyton.solawi.bid.module.banking.data.api.*
import org.solyton.solawi.bid.module.banking.data.api.MandateStatus.*
import org.solyton.solawi.bid.module.banking.data.api.PaymentExecutionStatus.*
import org.solyton.solawi.bid.module.banking.data.api.SepaSequenceType.*
import org.solyton.solawi.bid.module.banking.data.bankaccount.AccountType
import org.solyton.solawi.bid.module.banking.data.bankaccount.BankAccount
import org.solyton.solawi.bid.module.banking.data.creditor.identifier.CreditorIdentifier
import org.solyton.solawi.bid.module.banking.data.download.Download
import org.solyton.solawi.bid.module.banking.data.fiscalyear.FiscalYear
import org.solyton.solawi.bid.module.banking.data.legalentity.LegalEntity
import org.solyton.solawi.bid.module.banking.data.legalentity.LegalEntityType
import org.solyton.solawi.bid.module.banking.data.sepa.MandateStatus
import org.solyton.solawi.bid.module.banking.data.sepa.PaymentExecutionStatus
import org.solyton.solawi.bid.module.banking.data.sepa.SepaSequenceType
import org.solyton.solawi.bid.module.banking.data.sepa.SuccessorKind
import org.solyton.solawi.bid.module.banking.data.sepa.collection.SepaCollection
import org.solyton.solawi.bid.module.banking.data.sepa.mandate.SepaMandate
import org.solyton.solawi.bid.module.banking.data.sepa.message.SepaMessage
import org.solyton.solawi.bid.module.banking.data.sepa.message.SepaMessageStatus
import org.solyton.solawi.bid.module.banking.data.sepa.message.SepaMessageString
import org.solyton.solawi.bid.module.banking.data.sepa.message.SepaMessageVersion
import org.solyton.solawi.bid.module.banking.data.sepa.payment.SepaPayment
import org.solyton.solawi.bid.module.banking.data.sepa.payment.SepaPaymentLink
import org.solyton.solawi.bid.module.user.data.transform.toDomainType
import org.solyton.solawi.bid.module.banking.data.api.SepaMessageVersion as ApiSepaMessageVersion


/**
 * Converts an instance of ApiFiscalYears to a domain-level representation.
 *
 * Maps each `ApiFiscalYear` item within the `all` list property to its domain
 * type, utilizing the `toDomainType` extension function defined for `ApiFiscalYear`.
 *
 * @receiver ApiFiscalYears instance containing a list of ApiFiscalYear objects to convert.
 * @return A list of domain-level FiscalYear objects.
 */
fun ApiFiscalYears.toDomainType() = all.map { it.toDomainType() }

/**
 * Converts an instance of ApiFiscalYear to its corresponding domain-level representation.
 *
 * Maps the properties of `ApiFiscalYear` to the `FiscalYear` data class, producing
 * a new instance of `FiscalYear` with the same values for `id`, `legalEntityId`,
 * `start`, and `end`.
 *
 * @receiver ApiFiscalYear instance to convert.
 * @return A domain-level FiscalYear object containing the mapped data.
 */
fun ApiFiscalYear.toDomainType() = FiscalYear(id, legalEntityId, start, end)

/**
 * Converts an instance of ApiBankAccounts to a list of BankAccount domain objects.
 * This function maps each ApiBankAccount contained in the `all` property of ApiBankAccounts
 * into its corresponding BankAccount representation.
 *
 * @receiver The ApiBankAccounts instance containing the data to be transformed.
 * @return A list of BankAccount objects derived from the ApiBankAccounts instance.
 */
fun ApiBankAccounts.toDomainType() = all.map { it.toDomainType() }

/**
 * Converts an instance of ApiBankAccount to a BankAccount domain object.
 * This function transforms the API representation of a bank account into its corresponding domain model.
 *
 * @receiver The ApiBankAccount instance containing the data to be transformed.
 * @return A BankAccount object derived from the ApiBankAccount instance.
 */
fun ApiBankAccount.toDomainType() = BankAccount(
    id,
    userId,
    iban,
    bic,
    accountHolder?:"",
    isActive,
    accountType.toDomainType(),
    description,
)

fun ApiAccountType.toDomainType(): AccountType = when(this) {
    ApiAccountType.DEBTOR -> AccountType.DEBTOR
    ApiAccountType.CREDITOR -> AccountType.CREDITOR
}

fun AccountType.toApiType(): ApiAccountType = when(this) {
    AccountType.DEBTOR -> ApiAccountType.DEBTOR
    AccountType.CREDITOR -> ApiAccountType.CREDITOR
}

fun ApiLegalEntity.toDomainType(): LegalEntity = LegalEntity(
    legalEntityId,
    partyId,
    name,
    legalForm,
    legalEntityType.toDomainType(),
    address.toDomainType()
)

fun ApiLegalEntityType.toDomainType(): LegalEntityType = when(this) {
    ApiLegalEntityType.HUMAN -> LegalEntityType.HUMAN
    ApiLegalEntityType.ORGANIZATION -> LegalEntityType.ORGANIZATION
}

fun LegalEntityType.toApiType(): ApiLegalEntityType = when(this) {
    LegalEntityType.HUMAN -> ApiLegalEntityType.HUMAN
    LegalEntityType.ORGANIZATION -> ApiLegalEntityType.ORGANIZATION
}

fun ApiCreditorIdentifier.toDomainType(): CreditorIdentifier = CreditorIdentifier(
    creditorIdentifierId,
    legalEntityId,
    creditorId,
    validFrom,
    validUntil,
    isActive
)

fun ApiSepaMandates.toDomainType(): List<SepaMandate> = all.map { it.toDomainType() }

fun ApiSepaMandate.toDomainType(): SepaMandate = SepaMandate(
    sepaMandateId = sepaMandateId,
    debtorBankAccountId = debtorBankAccountId,
    debtorName = debtorName,
    mandateReference = mandateReference,
    signedAt = signedAt,
    validFrom = validFrom,
    validUntil = validUntil,
    lastUsedAt = lastUsedAt,
    status = status.toDomainType(),
    isActive = isActive,
    amendmentOf = amendmentOf,
    collectionIds = collectionIds,
    referenceIds = referenceIds
)

fun ApiSepaPayments.toDomainType(): List<SepaPayment> = all.map { it.toDomainType() }

fun ApiSepaPayment.toDomainType(): SepaPayment = SepaPayment(
    sepaPaymentId = sepaPaymentId,
    sepaMandateId = sepaMandateId,
    sepaCollectionId = sepaCollectionId,
    amount = amount,
    executionDate = executionDate,
    sequenceType = sequenceType.toDomainType(),
    status = status.toDomainType(),
    failureReason = failureReason,
    endToEndId = endToEndId,
    messageIdentifier = messageIdentifier,
    sepaMessageId = sepaMessageId,
    nextPeriodSuccessorId = nextPeriodSuccessorId,
    retrySuccessorId = retrySuccessorId,
    mergeSuccessorId = mergeSuccessorId,
)

fun ApiSepaPaymentLinks.toDomainType(): List<SepaPaymentLink> = all.map { it.toDomainType() }

fun ApiSepaPaymentLink.toDomainType(): SepaPaymentLink = SepaPaymentLink(
    successorId = successorId,
    predecessorId = predecessorId,
    kind = kind.toDomainType()
)

fun SepaPaymentLink.toApiType(): ApiSepaPaymentLink = ApiSepaPaymentLink(
    successorId = successorId,
    predecessorId = predecessorId,
    kind = kind.toApiType()
)

fun ApiSuccessorKind.toDomainType(): SuccessorKind = when(this) {
    ApiSuccessorKind.NEXT_PERIOD -> SuccessorKind.NEXT_PERIOD
    ApiSuccessorKind.RETRY -> SuccessorKind.RETRY
    ApiSuccessorKind.MERGE -> SuccessorKind.MERGE
    ApiSuccessorKind.AD_HOC -> SuccessorKind.AD_HOC
}

fun SuccessorKind.toApiType(): ApiSuccessorKind = when(this) {
    SuccessorKind.NEXT_PERIOD -> ApiSuccessorKind.NEXT_PERIOD
    SuccessorKind.RETRY -> ApiSuccessorKind.RETRY
    SuccessorKind.MERGE -> ApiSuccessorKind.MERGE
    SuccessorKind.AD_HOC -> ApiSuccessorKind.AD_HOC
}

fun ApiSepaCollection.toDomainType(): SepaCollection = SepaCollection(
    sepaCollectionId = sepaCollectionId,
    creditorIdentifierId = creditorIdentifierId,
    creditorBankAccountId = creditorBankAccountId,
    mandateReferencePrefix = mandateReferencePrefix,
    collectionKey = collectionKey,
    remittanceInformation = remittanceInformation,
    sepaSequenceType = sepaSequenceType.toDomainType(),
    localInstrument = localInstrument,
    chargeBearer = chargeBearer,
    requestedCollectionDay = requestedCollectionDay,
    leadTimesDays = leadTimeDays,
    purposeCode = purposeCode,
    isActive = isActive,
    sepaMandates = sepaMandates?.all?.map{it.toDomainType()}.orEmpty(),
    sepaPayments = sepaPayments?.all?.map{it.toDomainType()}.orEmpty(),
    referenceIds = referenceIds,
    mandateReferenceIds = mandateReferenceIds
)

fun ApiMandateStatus.toDomainType(): MandateStatus = when(this) {
    ACTIVE -> MandateStatus.ACTIVE
    REVOKED -> MandateStatus.REVOKED
    EXPIRED -> MandateStatus.EXPIRED
    SUSPENDED -> MandateStatus.SUSPENDED
}

fun MandateStatus.toApyType(): ApiMandateStatus = when(this) {
    MandateStatus.ACTIVE -> ACTIVE
    MandateStatus.REVOKED -> REVOKED
    MandateStatus.EXPIRED -> EXPIRED
    MandateStatus.SUSPENDED -> SUSPENDED
}

fun ApiSepaSequenceType.toDomainType(): SepaSequenceType = when(this) {
    UNCLEAR -> SepaSequenceType.UNCLEAR
    FRST -> SepaSequenceType.FRST
    RCUR -> SepaSequenceType.RCUR
    OOFF -> SepaSequenceType.OOFF
    FNAL -> SepaSequenceType.FNAL
}

fun SepaSequenceType.toApiType(): ApiSepaSequenceType = when(this) {
    SepaSequenceType.UNCLEAR -> UNCLEAR
    SepaSequenceType.FRST -> FRST
    SepaSequenceType.RCUR -> RCUR
    SepaSequenceType.OOFF -> OOFF
    SepaSequenceType.FNAL -> FNAL
}

fun ApiPaymentExecutionStatus.toDomainType(): PaymentExecutionStatus = when(this) {
    MESSAGE_CREATED -> PaymentExecutionStatus.MESSAGE_CREATED
    CREATED -> PaymentExecutionStatus.CREATED
    SENT -> PaymentExecutionStatus.SENT
    CONFIRMED -> PaymentExecutionStatus.CONFIRMED
    PAYED_MANUALLY -> PaymentExecutionStatus.PAYED_MANUALLY
    FAILED -> PaymentExecutionStatus.FAILED
    PENDING -> PaymentExecutionStatus.PENDING
    DROPPED -> PaymentExecutionStatus.DROPPED
    MESSAGE_SETTLED -> PaymentExecutionStatus.MESSAGE_SETTLED
}

fun PaymentExecutionStatus.toApiType(): ApiPaymentExecutionStatus = when(this) {
    PaymentExecutionStatus.MESSAGE_CREATED -> MESSAGE_CREATED
    PaymentExecutionStatus.CREATED -> CREATED
    PaymentExecutionStatus.SENT -> SENT
    PaymentExecutionStatus.CONFIRMED -> CONFIRMED
    PaymentExecutionStatus.PAYED_MANUALLY -> PAYED_MANUALLY
    PaymentExecutionStatus.FAILED -> FAILED
    PaymentExecutionStatus.PENDING -> PENDING
    PaymentExecutionStatus.DROPPED -> DROPPED
    PaymentExecutionStatus.MESSAGE_SETTLED -> MESSAGE_SETTLED
}

fun ApiSepaMessageString.toDomainType(): SepaMessageString = SepaMessageString(
    version = version.toDomainType(),
    message = message,
    download = Download.Start
)

fun SepaMessageString.toApiType(): ApiSepaMessageString = ApiSepaMessageString(
    version = version.toApiType(),
    message = message
)

fun ApiSepaMessageVersion.toDomainType(): SepaMessageVersion = when(this) {
    is ApiSepaMessageVersion.PAIN008 -> SepaMessageVersion.PAIN008

}

fun SepaMessageVersion.toApiType(): ApiSepaMessageVersion = when(this) {
    SepaMessageVersion.PAIN008 -> ApiSepaMessageVersion.PAIN008
}

fun ApiSepaMessage.toDomainType(): SepaMessage = SepaMessage(
    sepaMessageId = sepaMessageId,
    messageIdentifier = messageIdentifier,
    remittanceInformation = remittanceInformation,
    executionDate = executionDate,
    status = status.toDomainType(),
    totalAmount = totalAmount,
    numberOfPayments = numberOfPayments,
    paymentIds = paymentIds
)

fun ApiSepaMessages.toDomainType(): List<SepaMessage> = all.map { it.toDomainType() }

fun ApiSepaMessageStatus.toDomainType(): SepaMessageStatus = when(this) {
    ApiSepaMessageStatus.CREATED -> SepaMessageStatus.CREATED
    ApiSepaMessageStatus.SENT -> SepaMessageStatus.SENT
    ApiSepaMessageStatus.PENDING -> SepaMessageStatus.PENDING
    ApiSepaMessageStatus.CONFIRMED -> SepaMessageStatus.CONFIRMED
    ApiSepaMessageStatus.FAILED -> SepaMessageStatus.FAILED
    ApiSepaMessageStatus.SETTLED -> SepaMessageStatus.SETTLED
    ApiSepaMessageStatus.MERGED -> SepaMessageStatus.MERGED
}

fun SepaMessageStatus.toApiType(): ApiSepaMessageStatus = when(this) {
    SepaMessageStatus.CREATED -> ApiSepaMessageStatus.CREATED
    SepaMessageStatus.SENT -> ApiSepaMessageStatus.SENT
    SepaMessageStatus.PENDING -> ApiSepaMessageStatus.PENDING
    SepaMessageStatus.CONFIRMED -> ApiSepaMessageStatus.CONFIRMED
    SepaMessageStatus.FAILED -> ApiSepaMessageStatus.FAILED
    SepaMessageStatus.SETTLED -> ApiSepaMessageStatus.SETTLED
    SepaMessageStatus.MERGED -> ApiSepaMessageStatus.MERGED
}
